/**
 * O que aconteceu nesta aba antes do aluno clicar em "reportar problema".
 *
 * Um relato de beta tester chega como "deu erro" — sem stack, sem rota, sem
 * horário. Este módulo guarda os dois fatos que transformam esse relato em
 * diagnóstico: os erros que o `GlobalErrorHandler` interceptou e por onde o
 * usuário passou. Em memória, só desta aba, e com teto: o objetivo é caber num
 * e-mail, não ser um segundo sistema de observabilidade (isso é o PostHog).
 *
 * O filtro é o mesmo de `ignored-errors.ts` de propósito — um relatório cheio
 * de "Java object is gone" enterra o erro de verdade no meio do ruído de
 * terceiros.
 */

import { isIgnoredError } from "./ignored-errors"

/** Erros guardados. Além disso, o mais antigo sai. */
export const SESSION_ERROR_LIMIT = 15

/** Rotas guardadas. Além disso, a mais antiga sai. */
export const ROUTE_TRAIL_LIMIT = 10

export const MESSAGE_MAX_LENGTH = 300
export const STACK_MAX_LENGTH = 900
export const PATH_MAX_LENGTH = 200

export type SessionErrorSource = "error" | "unhandledrejection"

export interface SessionError {
  source: SessionErrorSource
  message: string
  stack: string | null
  /** ISO da primeira ocorrência desta mensagem na sequência atual. */
  firstAt: string
  /** ISO da última ocorrência. Igual a `firstAt` quando `count` é 1. */
  lastAt: string
  /** Repetições consecutivas colapsadas numa entrada só. */
  count: number
}

export interface RouteVisit {
  path: string
  at: string
}

const sessionErrors: SessionError[] = []
const routeTrail: RouteVisit[] = []

function truncate(value: string, max: number): string {
  return value.length <= max ? value : `${value.slice(0, max)}… [truncado]`
}

function messageOf(error: unknown): string {
  if (error instanceof Error) return error.message
  if (typeof error === "string") return error
  try {
    return JSON.stringify(error) ?? String(error)
  } catch {
    return String(error)
  }
}

/**
 * Registra um erro já reconhecido como nosso.
 *
 * Repetição consecutiva da mesma mensagem vira contador em vez de entrada
 * nova: um loop de render dispara o mesmo erro dezenas de vezes e sozinho
 * empurraria para fora do buffer tudo que veio antes — justamente o contexto
 * que explica o loop.
 */
export function recordSessionError(
  error: unknown,
  source: SessionErrorSource
): void {
  if (isIgnoredError(error)) return

  const message = truncate(messageOf(error), MESSAGE_MAX_LENGTH)
  const at = new Date().toISOString()

  const last = sessionErrors[sessionErrors.length - 1]
  if (last && last.message === message && last.source === source) {
    last.count += 1
    last.lastAt = at
    return
  }

  sessionErrors.push({
    source,
    message,
    stack:
      error instanceof Error && error.stack
        ? truncate(error.stack, STACK_MAX_LENGTH)
        : null,
    firstAt: at,
    lastAt: at,
    count: 1,
  })

  if (sessionErrors.length > SESSION_ERROR_LIMIT) sessionErrors.shift()
}

export function readSessionErrors(): SessionError[] {
  return sessionErrors.map((entry) => ({ ...entry }))
}

/** Rota repetida (remontagem, replace da mesma URL) não vira entrada nova. */
export function recordVisitedRoute(path: string): void {
  const trimmed = truncate(path, PATH_MAX_LENGTH)
  const last = routeTrail[routeTrail.length - 1]
  if (last?.path === trimmed) return

  routeTrail.push({ path: trimmed, at: new Date().toISOString() })
  if (routeTrail.length > ROUTE_TRAIL_LIMIT) routeTrail.shift()
}

export function readVisitedRoutes(): RouteVisit[] {
  return routeTrail.map((visit) => ({ ...visit }))
}

/** Só para testes: o buffer é global e vazaria de um caso para o outro. */
export function resetSessionDiagnostics(): void {
  sessionErrors.length = 0
  routeTrail.length = 0
}
